import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import Loading from '../../components/ui/Loading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import Pagination from '../../components/ui/Pagination';
import { getAnnouncementList } from '../../services/api/copyright';

const CopyrightAnnouncements = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data, loading, error, refetch } = useAPI(
    () => getAnnouncementList({ page: currentPage, pageSize }),
    [currentPage, i18n.language]
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAnnouncementClick = (announcement) => {
    if (announcement.noticeUrl) {
      window.open(announcement.noticeUrl, '_blank', 'noopener noreferrer');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  // 根据当前语言获取公告标题
  const getAnnouncementTitle = (announcement) => {
    if (!announcement.i18nInfo || !Array.isArray(announcement.i18nInfo)) {
      return announcement.title || '';
    }
    
    // 获取当前语言
    const currentLocale = i18n.language;
    
    // 查找对应语言的i18nInfo
    // 首先尝试严格匹配
    let i18nItem = announcement.i18nInfo.find(item => item.locale === currentLocale);
    
    // 如果严格匹配失败，尝试使用语言代码的前两个字符进行匹配（例如 "en" 匹配 "en-US"）
    if (!i18nItem && currentLocale.length >= 2) {
      const languageCode = currentLocale.substring(0, 2);
      i18nItem = announcement.i18nInfo.find(item => item.locale.substring(0, 2) === languageCode);
    }
    
    // 如果找到对应语言的信息，返回其标题
    if (i18nItem) {
      return i18nItem.title;
    }
    
    // 如果没有找到对应语言的信息，返回默认标题或第一个i18nInfo的标题
    return announcement.title || (announcement.i18nInfo[0]?.title || '');
  };

  // 根据当前语言获取公告内容
  const getAnnouncementContent = (announcement) => {
    if (!announcement.i18nInfo || !Array.isArray(announcement.i18nInfo)) {
      return announcement.content || '';
    }
    
    // 获取当前语言
    const currentLocale = i18n.language;
    
    // 查找对应语言的i18nInfo
    // 首先尝试严格匹配
    let i18nItem = announcement.i18nInfo.find(item => item.locale === currentLocale);
    
    // 如果严格匹配失败，尝试使用语言代码的前两个字符进行匹配（例如 "en" 匹配 "en-US"）
    if (!i18nItem && currentLocale.length >= 2) {
      const languageCode = currentLocale.substring(0, 2);
      i18nItem = announcement.i18nInfo.find(item => item.locale.substring(0, 2) === languageCode);
    }
    
    // 如果找到对应语言的信息，返回其内容
    if (i18nItem) {
      return i18nItem.content;
    }
    
    // 如果没有找到对应语言的信息，返回默认内容或第一个i18nInfo的内容
    return announcement.content || (announcement.i18nInfo[0]?.content || '');
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center min-h-[400px]">
          <Loading />
        </div>
    );
  }

  if (error) {
    return (
        <div className="container mx-auto px-4 py-8">
          <ErrorMessage 
            message={error} 
            onRetry={refetch}
          />
        </div>
    );
  }

  const { announcements = [], pagination = {} } = data || {};

  return (
      <div className="container mx-auto px-4 py-8">
        {/* 页面标题 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('copyright.announcements.title', '登记公示')}
          </h1>
        </div>

        {/* 公示列表 */}
        <div className="space-y-4 mb-8">
          {announcements.length > 0 ? (
            announcements.map((announcement) => (
              <div
                key={announcement.id || announcement.noticeId}
                className={`bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow ${announcement.noticeUrl ? 'cursor-pointer' : ''}`}
                onClick={() => handleAnnouncementClick(announcement)}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1 pr-4 hover:text-blue-600 transition-colors">
                    📋 {getAnnouncementTitle(announcement)}
                  </h3>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500">
              {t('copyright.announcements.noData')}
            </div>
          )}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
  );
};

export default CopyrightAnnouncements; 